/**
 * BottomSheetPopup – canonical in-app modal (all feature modals should use this; `Modal` only here).
 *
 * Pattern (aligns with `sharedStyles.ts` / atomic rules):
 * - **`sheetHeightMode` `fill` (default):** Sheet uses a **resolved pixel height** (88% of window, or `sheetStyle.maxHeight` if numeric / `%` string) so the inner `sheetBody` can use `flex: 1`. Without this, `ScrollView`/`FlatList` with `flex: 1` collapse to zero height (RN layout). Put scrollable content in `ScrollView` / `FlatList` inside `children`; they should use `flex: 1` + `minHeight: 0` when filling the body.
 * - **`sheetHeightMode` `fitContent`:** Sheet height follows content up to the same max cap; body is a plain **`View`** (not `ScrollView`) so **iOS `UIDatePicker` wheels** keep vertical touches — a parent `ScrollView` steals those gestures and `onChange` may never fire. Nest your own `ScrollView` in `children` only if you need overflow scroll.
 * - Footer primary/secondary actions: use `BottomSheetPopupFooter` (safe-area aware) or
 *   `createStackedFooterPrimarySurfaceStyle` / `createStackedFooterSecondarySurfaceStyle` from `sharedStyles`.
 * - Surfaces use `useThemeColors()` via parent; do not use static `appTheme.colors` for fills on theme-bound UI.
 *
 * Exceptions (do not refactor blindly): `AuthSheetHost` (custom slide + credential chrome), `MenuFlowScreen` overlays.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, Modal, ViewStyle, Platform, useWindowDimensions, KeyboardAvoidingView, Keyboard, Pressable, Dimensions, type StyleProp } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SvgIcon } from './SvgIcon';
import { Clickable } from './Clickable';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { appTheme, useThemeColors } from '../../shared/theme';
import { useTranslation } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import { resolveWebLayoutWidth } from '../../shared/platform/webLayout';

/** Resolve a concrete pixel height so the sheet column can give `flex: 1` children a bounded area (RN collapses them otherwise). */
function resolveSheetPixelHeight(window_height: number, sheet_style?: ViewStyle): number {
  const cap = Math.round(window_height * 0.88);
  const mh = sheet_style?.maxHeight;
  if (mh == null) {
    return cap;
  }
  if (typeof mh === 'number') {
    return Math.min(mh, cap);
  }
  if (typeof mh === 'string') {
    const m = mh.trim().match(/^([\d.]+)%$/);
    if (m) {
      const p = parseFloat(m[1]) / 100;
      if (!Number.isNaN(p)) {
        return Math.round(window_height * Math.min(p, 0.88));
      }
    }
  }
  return cap;
}

/** `fill` = fixed height to max cap (default, for `FlatList` / `flex:1` bodies). `fitContent` = shrink to content, scroll when taller than cap. */
export type BottomSheetHeightMode = 'fill' | 'fitContent';

export interface BottomSheetPopupProps {
  visible: boolean;
  onClose: () => void;
  onRequestClose?: () => void;
  /** Optional title in header. If omitted, header still shows close button unless showCloseButton is false. */
  title?: string;
  /** Show close (X) button in header. Default true. */
  showCloseButton?: boolean;
  /** Drag handle pill above header (aligns with `createBottomSheetChromeStyles` sheets). */
  showDragHandle?: boolean;
  /** 'slide' (from bottom) or 'fade'. Default 'slide'. */
  animationType?: 'slide' | 'fade';
  /**
   * How the sheet uses vertical space. Default `fill` (full resolved cap) for list-heavy modals.
   * Use `fitContent` when the sheet should only be as tall as its content (up to the same max cap), with internal scroll if needed.
   */
  sheetHeightMode?: BottomSheetHeightMode;
  /** Sheet container style (e.g. maxHeight, height). Merged after defaults; default maxHeight = `appTheme.modal.sheetMaxHeight`. */
  sheetStyle?: ViewStyle;
  /** Header style override. */
  headerStyle?: ViewStyle;
  /** Override modal dimmer color (defaults to `colors.backdrop` from theme). */
  backdropColor?: string;
  /**
   * Fires after the sheet has finished its dismiss animation (slide down).
   * Use to unblock underlying UI (`pointerEvents`) or chain navigation so the `Modal` is fully gone first.
   */
  onDismissComplete?: () => void;
  /** When set, overrides the default modal top corner radius (e.g. v3 studio sheets). */
  sheetTopBorderRadius?: number;
  /**
   * iOS: frosted-glass style header row. Android: translucent surface. Default row remains solid; opt-in only.
   * Used by v3 calendar modals; leave unset for existing sheets.
   */
  frostedSheetHeader?: boolean;
  /**
   * Reserve space below sheet body for the home indicator / gesture bar.
   * Rendered as an explicit bottom bar (not `paddingBottom`) so pinned footers stay aligned on iOS + Android modals.
   * Set false when children already apply `resolveBottomSheetSafeInset()` to footers.
   */
  padBottomSafeArea?: boolean;
  /**
   * Wrap sheet body in `KeyboardAvoidingView` so footers stay above the keyboard on iOS.
   * For Android, pair with `ScrollView`/`FlatList` `keyboardShouldPersistTaps` and optional extra
   * `contentContainerStyle.paddingBottom` from keyboard height (see auth sheets / `useAuthSheetKeyboardInset`).
   */
  keyboardAvoiding?: boolean;
  children: React.ReactNode;
}

const modalTokens = appTheme.modal;

/** Aligns with RN Modal `animationType="slide"` dismiss duration so `onDismissComplete` runs after the sheet is off-screen. */
/** Match iOS slide dismiss duration on both platforms. */
const MODAL_SLIDE_DISMISS_MS = 340;
const MIN_SHEET_PIXEL_HEIGHT = 280;
/** RN `Modal` often reports `insets.bottom === 0` on Android gesture nav — keep a floor. */
const ANDROID_MODAL_BOTTOM_INSET_FLOOR = 24;

/** Bottom inset for sheet footers and the sheet safe-area bar (iOS + Android modal). */
export function resolveBottomSheetSafeInset(raw_bottom_inset: number): number {
  const themed_floor = appTheme.spacing.md;
  if (Platform.OS === 'android') {
    return Math.max(raw_bottom_inset, themed_floor, ANDROID_MODAL_BOTTOM_INSET_FLOOR);
  }
  return Math.max(raw_bottom_inset, themed_floor);
}

export type BottomSheetPopupFooterProps = {
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
};

/** Pinned footer row for `BottomSheetPopup` — pairs with the sheet's bottom safe-area bar (`padBottomSafeArea`). */
export function BottomSheetPopupFooter({ children, style }: BottomSheetPopupFooterProps) {
  const colors = useThemeColors() ?? appTheme.colors;
  return (
    <View
      style={[
        footerStyles.root,
        {
          borderTopColor: colors.border,
          backgroundColor: colors.surface,
        },
        style,
      ]}>
      {children}
    </View>
  );
}

const footerStyles = StyleSheet.create({
  root: {
    flexShrink: 0,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: appTheme.spacing.md,
    paddingTop: appTheme.spacing.md,
    paddingHorizontal: appTheme.spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
});

function resolveWindowHeight(window_height: number): number {
  if (window_height > 0) {
    return window_height;
  }
  return Dimensions.get('window').height;
}

export const BottomSheetPopup: React.FC<BottomSheetPopupProps> = ({
  visible,
  onClose,
  onRequestClose,
  title,
  showCloseButton = true,
  showDragHandle = false,
  animationType = 'slide',
  sheetHeightMode = 'fill',
  sheetStyle,
  headerStyle,
  backdropColor,
  onDismissComplete,
  padBottomSafeArea = true,
  keyboardAvoiding = false,
  sheetTopBorderRadius,
  frostedSheetHeader = false,
  children,
}) => {
  const { t } = useTranslation();
  const rtl = useRtl();
  const colors = useThemeColors() ?? appTheme.colors;
  const top_radius = sheetTopBorderRadius ?? modalTokens.sheetBorderRadius;
  const overlay_backdrop = backdropColor ?? colors.backdrop;
  const insets = useSafeAreaInsets();
  const { width: window_width, height: window_height_raw } = useWindowDimensions();
  const window_height = resolveWindowHeight(window_height_raw);
  const [should_render, set_should_render] = useState(visible);

  useEffect(() => {
    if (visible) {
      set_should_render(true);
      return;
    }
    if (!should_render) {
      return;
    }
    const dismiss_ms = animationType === 'slide' ? MODAL_SLIDE_DISMISS_MS : 200;
    const id = setTimeout(() => {
      onDismissComplete?.();
      set_should_render(false);
    }, dismiss_ms);
    return () => clearTimeout(id);
  }, [visible, should_render, onDismissComplete, animationType]);
  const sheet_bottom_inset = padBottomSafeArea ? resolveBottomSheetSafeInset(insets.bottom) : 0;
  const sheet_pixel_height = Math.max(MIN_SHEET_PIXEL_HEIGHT, resolveSheetPixelHeight(window_height, sheetStyle));
  const flattened_sheet_style = StyleSheet.flatten(sheetStyle) as ViewStyle | undefined;
  const sheet_bg =
    typeof flattened_sheet_style?.backgroundColor === 'string'
      ? flattened_sheet_style.backgroundColor
      : colors.surface;
  const [chrome_height, set_chrome_height] = useState(0);
  const on_chrome_layout = useCallback((e: { nativeEvent: { layout: { height: number } } }) => {
    set_chrome_height(e.nativeEvent.layout.height);
  }, []);
  const chrome_estimate = (showDragHandle ? 28 : 0) + 56;
  const effective_chrome = chrome_height > 0 ? chrome_height : chrome_estimate;
  const body_scroll_max_height = Math.max(160, sheet_pixel_height - effective_chrome - sheet_bottom_inset);
  const is_web = Platform.OS === 'web';
  /** Web: use phone-column width — full browser width stretches guest/auth sheets; `%` children also need a real pixel width. */
  const layout_width = resolveWebLayoutWidth(window_width);
  const sheet_width = is_web ? layout_width : ('100%' as const);

  const sheet_shell = StyleSheet.flatten([
    styles.sheet,
    sheetStyle,
    {
      backgroundColor: sheet_bg,
      borderTopLeftRadius: top_radius,
      borderTopRightRadius: top_radius,
      zIndex: 2,
      elevation: Platform.OS === 'android' ? 24 : 0,
      maxHeight: sheet_pixel_height,
      width: sheet_width,
      maxWidth: is_web ? layout_width : undefined,
      alignSelf: is_web ? ('center' as const) : ('stretch' as const),
      ...(sheetHeightMode === 'fill' ? { height: sheet_pixel_height } : {}),
    },
  ]);

  const header_row_inner = (
    <>
      {title != null && title !== '' ? (
        <Text
          style={[
            styles.title,
            frostedSheetHeader ? styles.studioHeaderTitle : null,
            {
              color: colors.text,
              textAlign: rtl.textAlign,
              writingDirection: rtl.writingDirection,
              marginEnd: appTheme.spacing.sm,
            },
          ]}
          numberOfLines={2}>
          {title}
        </Text>
      ) : (
        <View style={styles.titleSpacer} />
      )}
      {showCloseButton ? (
        <Clickable
          onPress={onClose}
          style={styles.closeWrap}
          accessibilityLabel={t('common.close')}
          accessibilityRole="button">
          <View style={styles.closeBtn}>
            <SvgIcon name="x" size={24} color={colors.text} />
          </View>
        </Clickable>
      ) : null}
    </>
  );

  const header_row = (
    <View
      style={[
        styles.header,
        {
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: colors.borderSubtle,
          backgroundColor: sheet_bg,
          padding: modalTokens.headerPadding,
        },
        headerStyle,
      ]}
    >
      {header_row_inner}
    </View>
  );

  const chrome_block = (
    <View onLayout={on_chrome_layout} style={styles.chromeMeasure}>
      {showDragHandle ? <View style={[styles.dragHandle, { backgroundColor: colors.border }]} /> : null}
      {header_row}
    </View>
  );

  /** Backdrop / Android back: dismiss IME first so the sheet stays open until the next dismiss gesture. */
  const dismiss_sheet_or_keyboard_first = useCallback(() => {
    if (Keyboard.isVisible()) {
      Keyboard.dismiss();
      return;
    }
    onClose();
  }, [onClose]);

  const handle_modal_request_close = useCallback(() => {
    if (Keyboard.isVisible()) {
      Keyboard.dismiss();
      return;
    }
    (onRequestClose ?? onClose)();
  }, [onClose, onRequestClose]);

  if (!should_render) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      animationType={animationType}
      transparent
      onRequestClose={handle_modal_request_close}
      presentationStyle="overFullScreen"
      statusBarTranslucent={Platform.OS === 'android'}
      hardwareAccelerated
    >
      <GestureHandlerRootView style={{ flex: 1 }} {...(Platform.OS === 'android' ? { unstable_forceActive: true } : {})}>
        <View
          style={[
            styles.overlay,
            { backgroundColor: overlay_backdrop },
            is_web
              ? {
                  position: 'fixed' as const,
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: window_width,
                  height: window_height,
                  maxWidth: window_width,
                }
              : null,
          ]}>
          <Pressable
            style={[StyleSheet.absoluteFill, styles.backdropPressable]}
            onPress={dismiss_sheet_or_keyboard_first}
            accessibilityLabel={t('common.close')}
            accessibilityRole="button"
          />
          <View style={sheet_shell} pointerEvents="box-none">
          {chrome_block}
          {sheetHeightMode === 'fitContent' ? (
            <View style={[styles.sheetBodyFitScroll, { maxHeight: body_scroll_max_height }]}>
              {keyboardAvoiding && Platform.OS === 'ios' ? (
                <KeyboardAvoidingView style={styles.keyboardAvoidingFill} behavior="padding">
                  {children}
                </KeyboardAvoidingView>
              ) : (
                children
              )}
            </View>
          ) : (
            <View style={styles.sheetBody}>
              {keyboardAvoiding && Platform.OS === 'ios' ? (
                <KeyboardAvoidingView style={styles.keyboardAvoidingFill} behavior="padding">
                  {children}
                </KeyboardAvoidingView>
              ) : (
                children
              )}
            </View>
          )}
          {sheet_bottom_inset > 0 ? (
            <View
              style={{
                height: sheet_bottom_inset,
                width: '100%',
                flexShrink: 0,
                backgroundColor: sheet_bg,
              }}
            />
          ) : null}
          </View>
        </View>
      </GestureHandlerRootView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    width: '100%',
    height: '100%',
    justifyContent: 'flex-end',
  },
  backdropPressable: {
    zIndex: 0,
  },
  sheet: {
    flexDirection: 'column',
  },
  chromeMeasure: {
    flexShrink: 0,
    width: '100%',
  },
  /** Fills space under header so ScrollView / FlatList with flex 1 receive a real height. */
  sheetBody: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    paddingTop: modalTokens.bodyPaddingTop,
  },
  sheetBodyFitScroll: {
    width: '100%',
    minHeight: 0,
    flexShrink: 0,
    paddingTop: modalTokens.bodyPaddingTop,
  },
  keyboardAvoidingFill: {
    flex: 1,
    minHeight: 0,
    width: '100%',
  },
  dragHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: appTheme.spacing.sm,
    marginBottom: appTheme.spacing.xs,
    flexShrink: 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    flexGrow: 0,
    flexShrink: 0,
  },
  closeWrap: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeBtn: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    ...appTheme.typography.h3,
    flex: 1,
    marginEnd: appTheme.spacing.sm,
  },
  /** v3 frosted header — slightly tighter, studio weight. */
  studioHeaderTitle: {
    fontSize: 17,
    fontWeight: '800',
    letterSpacing: -0.2,
  },
  frostedHeader: {
    position: 'relative' as const,
    overflow: 'hidden' as const,
  },
  frostedHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    width: '100%',
    zIndex: 1,
  },
  titleSpacer: {
    flex: 1,
  },
});

export default BottomSheetPopup;
